// src/pages/PrivacyPolicy.jsx
import React from "react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#181a1b] text-gray-100">
      <div className="max-w-3xl mx-auto pt-16 pb-20 px-4">
        <h1 className="text-3xl md:text-4xl font-bold text-yellow-400 mb-7">Privacy Policy</h1>

        <p className="text-sm text-gray-400 mb-8">
          Effective August 21, 2026
        </p>

        <p className="mb-6">
          This Privacy Policy explains how RetainAI (“we”, “us”, “our”) collects, uses, and safeguards
          your information when you use our website and services (the “Service”). By using RetainAI,
          you agree to the practices described below.
        </p>

        <h2 className="text-2xl font-bold text-yellow-300 mt-8 mb-2">Partner referrals</h2>
        <p className="mb-6">Following an approved referral link sets a signed, first-party cookie for up to 60 days. If you create a new account in that browser, we record the partner code and reconcile membership invoices, refunds and commissions. Partners do not receive your contacts, messages or account email through this program. Referral accounting records use a hashed account identifier and Stripe references and may be retained after deletion for reconciliation and to prevent repeat referral claims. You can clear the cookie in your browser.</p>
        <h2 className="text-2xl font-bold text-yellow-300 mt-8 mb-2">1. Information We Collect</h2>
        <ul className="list-disc ml-6 mb-6 space-y-2">
          <li><b>Account & Profile Data:</b> name, email, password (hashed), business details (e.g., business name, logo).</li>
          <li><b>Lead & Customer Data:</b> names, emails, phone numbers, tags/notes you add, message history, and appointment info.</li>
          <li><b>Usage Data:</b> app interactions, device/browser info, IP address, and diagnostic logs to improve performance.</li>
          <li><b>Cookies & Similar Tech:</b> essential cookies for authentication and session management; optional analytics (if enabled).</li>
          <li><b>Connected Services:</b> identifiers, authorization status, and event data needed for integrations you enable, such as WhatsApp, Google Calendar, email, and Stripe.</li>
        </ul>

        <h2 className="text-2xl font-bold text-yellow-300 mt-8 mb-2">2. How We Use Information</h2>
        <ul className="list-disc ml-6 mb-6 space-y-2">
          <li>Provide and maintain the Service (e.g., login, messaging, automations, appointments).</li>
          <li>Improve features, performance, and user experience.</li>
          <li>Communicate with you about updates, security alerts, and support.</li>
          <li>Process payments and invoicing if you subscribe.</li>
          <li>Generate AI-assisted drafts, recommendations, relationship summaries, and workflow suggestions that you review before use.</li>
          <li>Detect fraud, abuse, security incidents, failed automations, and service errors.</li>
          <li>Comply with legal obligations and enforce our terms.</li>
        </ul>

        <h2 className="text-2xl font-bold text-yellow-300 mt-8 mb-2">3. Legal Basis (EEA/UK)</h2>
        <p className="mb-6">
          Where applicable, we process data under legitimate interests (operating and improving the Service),
          contract necessity (providing subscribed features), and consent (e.g., marketing where required).
        </p>

        <h2 className="text-2xl font-bold text-yellow-300 mt-8 mb-2">4. Sharing & Processors</h2>
        <p className="mb-3">
          We do not sell your personal data. We may share data with trusted service providers who process it on our behalf:
        </p>
        <ul className="list-disc ml-6 mb-6 space-y-2">
          <li><b>Payments:</b> Stripe (subscription billing, invoices).</li>
          <li><b>Email:</b> SendGrid (transactional and notification emails).</li>
          <li><b>Calendars:</b> Google Calendar (optional user-connected feature).</li>
          <li><b>Messaging:</b> Meta/WhatsApp Cloud API (optional messaging feature).</li>
          <li><b>Infrastructure/Analytics:</b> hosting, error logging, usage analytics (if enabled).</li>
        </ul>
        <p className="mb-6">
          We require processors to protect data and use it only as instructed by us.
          We may disclose information if required by law or to protect rights, safety, and security.
        </p>

        <h2 className="text-2xl font-bold text-yellow-300 mt-8 mb-2">5. Google User Data</h2>
        <p className="mb-3">
          If you connect Google, RetainAI requests read-only access to the Google data needed for the feature you choose:
          your calendar list and calendar events for the calendar view, and your contacts for contact import. We use this data
          only to provide and improve those user-facing RetainAI features. RetainAI does not use Google user data for advertising,
          sell it, or allow people to read it except when needed to provide support or security with your permission, to comply
          with law, or through service providers acting under our instructions.
        </p>
        <p className="mb-6">
          Authorization credentials and synchronized data are retained only as needed to keep the integration working.
          Authorization credentials are encrypted at the application layer, and synchronized data is protected by tenant access
          controls and our hosting and storage safeguards. You can disconnect a Google integration in RetainAI settings,
          revoke RetainAI in your Google Account permissions, delete imported contacts, or delete your RetainAI account. These
          actions remove the active connection and associated stored credentials, subject to limited backup, legal, fraud-prevention,
          and security retention described below. RetainAI&apos;s use and transfer of information received from Google APIs complies
          with the{" "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            className="text-yellow-300 underline"
            target="_blank"
            rel="noreferrer"
          >
            Google API Services User Data Policy
          </a>
          , including its Limited Use requirements.
        </p>

        <h2 className="text-2xl font-bold text-yellow-300 mt-8 mb-2">6. Customer Responsibilities</h2>
        <p className="mb-6">
          RetainAI customers decide which contacts and messages they place in the Service and are responsible for having a lawful basis,
          required consent, and appropriate notices for customer communications. Do not upload payment-card data, government identifiers,
          health records, account passwords, or other sensitive information unless RetainAI expressly supports that use in writing.
        </p>

        <h2 className="text-2xl font-bold text-yellow-300 mt-8 mb-2">7. Data Retention</h2>
        <p className="mb-6">
          We retain personal data for as long as your account is active or as needed to provide the Service.
          Workspace data is deleted or de-identified after a valid deletion request and any displayed recovery period, subject to backup
          rotation and records we must retain for legal, accounting, fraud-prevention, dispute, or security purposes.
        </p>

        <h2 className="text-2xl font-bold text-yellow-300 mt-8 mb-2">8. Security</h2>
        <p className="mb-6">
          We use administrative, technical, and organizational safeguards including access controls, hashed passwords, session protection,
          encrypted provider connections, backups, monitoring, and incident procedures. No system is completely secure. If a breach creates
          a real risk of significant harm, RetainAI will investigate and provide legally required notices.
        </p>

        <h2 className="text-2xl font-bold text-yellow-300 mt-8 mb-2">9. Your Choices & Rights</h2>
        <ul className="list-disc ml-6 mb-6 space-y-2">
          <li>Access, correct, or delete profile data from your account settings or by contacting support.</li>
          <li>Unsubscribe from marketing communications (where applicable) using provided links.</li>
          <li>EEA/UK residents may have additional rights (e.g., objection, restriction, portability).</li>
        </ul>

        <h2 className="text-2xl font-bold text-yellow-300 mt-8 mb-2">10. International Transfers</h2>
        <p className="mb-6">
          If we transfer data internationally, we use appropriate safeguards (e.g., SCCs) to protect your information.
        </p>

        <h2 className="text-2xl font-bold text-yellow-300 mt-8 mb-2">11. Children’s Privacy</h2>
        <p className="mb-6">
          RetainAI is not directed to children under 13 (or minimum age in your region). We do not knowingly collect data from children.
        </p>

        <h2 className="text-2xl font-bold text-yellow-300 mt-8 mb-2">12. Changes to this Policy</h2>
        <p className="mb-6">
          We may update this policy from time to time. We’ll notify you of material changes by email or in-app notices.
        </p>

        <h2 className="text-2xl font-bold text-yellow-300 mt-8 mb-2">13. Contact</h2>
        <p className="mb-6">
          Privacy questions, access/correction requests, or complaints may be sent to{" "}
          <a href="mailto:owner@retainai.ca" className="text-yellow-300 underline">owner@retainai.ca</a>.
        </p>

        <p className="text-xs text-gray-400">
          This Privacy Policy is provided for general informational purposes and does not constitute legal advice.
        </p>
      </div>
    </div>
  );
}
